import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { ClearableInput } from './ui/clearable-input';
import { History, MessageSquare, Trash2, Check, Loader2, Search, Pencil, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Relative-time helper (de / en). Keeps the drawer self-contained.
function relativeTime(iso, lang) {
    if (!iso) return '';
    const then = new Date(iso);
    const diffMs = Date.now() - then.getTime();
    const diffMin = Math.round(diffMs / 60000);
    const diffH = Math.round(diffMs / 3600000);
    const diffD = Math.round(diffMs / 86400000);
    if (lang === 'de') {
        if (diffMin < 1) return 'gerade eben';
        if (diffMin < 60) return `vor ${diffMin} Min`;
        if (diffH < 24) return `vor ${diffH} Std`;
        if (diffD < 7) return `vor ${diffD} Tag${diffD === 1 ? '' : 'en'}`;
        return then.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffH < 24) return `${diffH}h ago`;
    if (diffD < 7) return `${diffD} day${diffD === 1 ? '' : 's'} ago`;
    return then.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Highlights search-term occurrences inside text. Returns a fragment-safe
// array of React-spans, with matched parts wrapped in <mark>.
function highlightMatch(text, term) {
    if (!text || !term || term.length < 2) return text;
    try {
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escaped})`, 'gi');
        const parts = text.split(regex);
        return parts.map((p, i) =>
            regex.test(p)
                ? <mark key={i} className="bg-amber-200 dark:bg-amber-700/60 text-foreground rounded px-0.5">{p}</mark>
                : p
        );
    } catch (_e) {
        return text;
    }
}

/**
 * Bottom-sheet (mobile) / side-drawer (desktop) listing all of the user's
 * Nanouk conversations. Tapping one resumes it as the active conversation
 * and replaces the on-screen chat with its persisted messages.
 *
 * Props:
 *   - activeId: current active conversation_id (so we can highlight it)
 *   - onResume(messages, conversationId): called after a successful resume
 *   - children: the trigger element (a button)
 */
export function CoachHistoryDrawer({ activeId, onResume }) {
    const { language } = useLanguage();
    const [open, setOpen] = useState(false);
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [resumingId, setResumingId] = useState(null);
    // Search state — debounced 300ms so we don't hammer the server while
    // the user types.
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null); // null = inactive, [] = no matches
    const [searching, setSearching] = useState(false);
    // Inline rename state — only one conversation can be in edit mode at a time
    const [editingId, setEditingId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [savingTitle, setSavingTitle] = useState(false);
    // Long-press timer ref (used for the tap+hold gesture on touch devices)
    const longPressTimerRef = useRef(null);
    const API = process.env.REACT_APP_BACKEND_URL;

    const loadConversations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/coach/conversations`);
            setConversations(res?.data?.conversations || []);
        } catch (e) {
            toast.error(language === 'de' ? 'Verlauf konnte nicht geladen werden' : 'Failed to load history');
        } finally {
            setLoading(false);
        }
    }, [API, language]);

    useEffect(() => {
        if (open) loadConversations();
    }, [open, loadConversations]);

    // Debounced search: hit the backend 300ms after the last keystroke.
    useEffect(() => {
        const term = searchQuery.trim();
        if (term.length < 2) {
            setSearchResults(null);
            setSearching(false);
            return;
        }
        let cancelled = false;
        setSearching(true);
        const timer = setTimeout(async () => {
            try {
                const res = await axios.get(
                    `${API}/api/coach/conversations/search`,
                    { params: { q: term, limit: 50 } }
                );
                if (cancelled) return;
                setSearchResults(res?.data?.conversations || []);
            } catch (_e) {
                if (cancelled) return;
                setSearchResults([]);
                toast.error(language === 'de' ? 'Suche fehlgeschlagen' : 'Search failed');
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [searchQuery, API, language]);

    // The list we actually render: search results when active, full list otherwise.
    const displayList = useMemo(
        () => (searchResults !== null ? searchResults : conversations),
        [searchResults, conversations]
    );
    const isSearchMode = searchResults !== null;

    const handleResume = async (conv) => {
        // While renaming, taps on the row are no-ops — user can still hit
        // ✓ / ✕ inside the edit field.
        if (editingId) return;
        if (conv.id === activeId) {
            setOpen(false);
            return;
        }
        setResumingId(conv.id);
        try {
            // 1) Mark as active server-side
            await axios.post(`${API}/api/coach/conversations/${conv.id}/activate`, null);
            // 2) Fetch its messages
            const msgRes = await axios.get(`${API}/api/coach/conversations/${conv.id}/messages?limit=200`);
            const persisted = (msgRes?.data?.messages || []).map(m => ({
                role: m.role,
                content: m.content,
                images: m.image_thumbnail ? [m.image_thumbnail] : undefined,
                persisted: true,
                createdAt: m.created_at,
            }));
            onResume?.(persisted, conv.id);
            toast.success(language === 'de' ? 'Konversation fortgesetzt' : 'Conversation resumed');
            setOpen(false);
        } catch (e) {
            toast.error(language === 'de' ? 'Fortsetzen fehlgeschlagen' : 'Resume failed');
        } finally {
            setResumingId(null);
        }
    };

    const handleDelete = async (conv, e) => {
        e.stopPropagation();
        const confirmMsg = language === 'de'
            ? `Diese Konversation mit ${conv.message_count} Nachrichten unwiderruflich löschen?`
            : `Permanently delete this conversation with ${conv.message_count} messages?`;
        if (!window.confirm(confirmMsg)) return;
        try {
            await axios.delete(`${API}/api/coach/conversations/${conv.id}`);
            setConversations(prev => prev.filter(c => c.id !== conv.id));
            toast.success(language === 'de' ? 'Konversation gelöscht' : 'Conversation deleted');
        } catch (err) {
            toast.error(language === 'de' ? 'Löschen fehlgeschlagen' : 'Delete failed');
        }
    };

    // Enter inline-edit mode for a conversation's title.
    const startEditing = (conv, e) => {
        if (e) e.stopPropagation();
        setEditingId(conv.id);
        setEditingTitle(conv.title || '');
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditingTitle('');
    };

    // Persist the renamed title. Empty string resets to auto-title.
    const saveTitle = async (conv) => {
        const newTitle = editingTitle.trim();
        // No-op if unchanged
        if (newTitle === (conv.title || '')) {
            cancelEditing();
            return;
        }
        setSavingTitle(true);
        try {
            const res = await axios.patch(
                `${API}/api/coach/conversations/${conv.id}`,
                { title: newTitle });
            const persisted = res?.data?.title ?? null;
            setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, title: persisted } : c));
            if (searchResults) {
                setSearchResults(prev => prev?.map(c => c.id === conv.id ? { ...c, title: persisted } : c));
            }
            toast.success(language === 'de' ? 'Titel gespeichert' : 'Title saved');
            cancelEditing();
        } catch (_e) {
            toast.error(language === 'de' ? 'Speichern fehlgeschlagen' : 'Save failed');
        } finally {
            setSavingTitle(false);
        }
    };

    // Tap-and-hold (500 ms) opens the inline edit on touch devices. Cancels
    // on early release / move so a normal tap still resumes the conversation.
    const startLongPress = (conv) => {
        if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = setTimeout(() => {
            startEditing(conv);
        }, 500);
    };
    const cancelLongPress = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    title={language === 'de' ? 'Konversationsverlauf' : 'Conversation history'}
                    aria-label={language === 'de' ? 'Konversationsverlauf' : 'Conversation history'}
                    data-testid="coach-history-btn"
                    className="text-muted-foreground hover:text-foreground"
                >
                    <History className="w-5 h-5" />
                </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
                <SheetHeader className="p-4 border-b">
                    <SheetTitle className="flex items-center gap-2 text-base">
                        <History className="w-4 h-4 text-primary" />
                        {language === 'de' ? 'Deine Konversationen' : 'Your conversations'}
                    </SheetTitle>
                    <div className="relative mt-2">
                        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        <ClearableInput
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={language === 'de' ? 'Verlauf durchsuchen…' : 'Search history…'}
                            className="h-9 pl-9 text-sm"
                            data-testid="coach-history-search"
                            clearTestId="coach-history-search-clear"
                        />
                    </div>
                    {isSearchMode && !searching && (
                        <p className="text-[11px] text-muted-foreground mt-1 px-1">
                            {language === 'de'
                                ? `${displayList.length} Treffer für „${searchQuery.trim()}"`
                                : `${displayList.length} matches for "${searchQuery.trim()}"`}
                        </p>
                    )}
                </SheetHeader>
                <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="coach-history-list">
                    {(loading || searching) && (
                        <div className="flex items-center justify-center py-12 text-muted-foreground">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            <span className="text-sm">
                                {searching
                                    ? (language === 'de' ? 'Suche…' : 'Searching…')
                                    : (language === 'de' ? 'Lade…' : 'Loading…')}
                            </span>
                        </div>
                    )}
                    {!loading && !searching && displayList.length === 0 && (
                        <div className="text-center py-12 px-4 text-muted-foreground">
                            <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
                            <p className="text-sm">
                                {isSearchMode
                                    ? (language === 'de'
                                        ? `Keine Treffer für „${searchQuery.trim()}".`
                                        : `No matches for "${searchQuery.trim()}".`)
                                    : (language === 'de'
                                        ? 'Noch keine Konversationen. Starte einfach den Chat — sobald Nanouk antwortet, erscheint sie hier.'
                                        : 'No conversations yet. Just start chatting — your conversations will appear here.')}
                            </p>
                        </div>
                    )}
                    {!loading && !searching && displayList.map((conv) => {
                        const isActive = conv.id === activeId;
                        const isResuming = resumingId === conv.id;
                        const isEditing = editingId === conv.id;
                        const titleText = conv.title || (language === 'de' ? 'Konversation' : 'Conversation');
                        return (
                            <div
                                key={conv.id}
                                role="button"
                                tabIndex={isResuming || isEditing ? -1 : 0}
                                onClick={() => handleResume(conv)}
                                onKeyDown={(e) => {
                                    if (isEditing) return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleResume(conv);
                                    }
                                }}
                                onTouchStart={() => !isEditing && startLongPress(conv)}
                                onTouchEnd={cancelLongPress}
                                onTouchMove={cancelLongPress}
                                onTouchCancel={cancelLongPress}
                                aria-disabled={isResuming}
                                data-testid={`coach-history-item-${conv.id}`}
                                className={`w-full text-left p-3 rounded-xl border transition-all cursor-pointer ${
                                    isActive
                                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                        : 'border-border hover:border-primary/40 hover:bg-muted/40'
                                } ${isResuming ? 'opacity-60 pointer-events-none' : ''}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            {isActive && (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-primary text-primary-foreground">
                                                    <Check className="w-2.5 h-2.5" />
                                                    {language === 'de' ? 'Aktiv' : 'Active'}
                                                </span>
                                            )}
                                            {conv.archived && !isActive && (
                                                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-muted text-muted-foreground">
                                                    {language === 'de' ? 'Archiviert' : 'Archived'}
                                                </span>
                                            )}
                                            <span className="text-[11px] text-muted-foreground">
                                                {relativeTime(conv.last_message_at, language)}
                                            </span>
                                        </div>
                                        {isEditing ? (
                                            <div
                                                className="flex items-center gap-1.5"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <input
                                                    type="text"
                                                    value={editingTitle}
                                                    onChange={(e) => setEditingTitle(e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            saveTitle(conv);
                                                        } else if (e.key === 'Escape') {
                                                            e.preventDefault();
                                                            cancelEditing();
                                                        }
                                                    }}
                                                    autoFocus
                                                    maxLength={80}
                                                    disabled={savingTitle}
                                                    placeholder={language === 'de' ? 'Titel eingeben…' : 'Enter title…'}
                                                    className="flex-1 min-w-0 text-sm font-medium px-2 py-1 rounded-md border border-primary/50 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                                                    data-testid={`coach-history-edit-input-${conv.id}`}
                                                />
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); saveTitle(conv); }}
                                                    disabled={savingTitle}
                                                    className="p-1.5 rounded-md text-primary hover:bg-primary/10 disabled:opacity-50"
                                                    title={language === 'de' ? 'Speichern' : 'Save'}
                                                    aria-label={language === 'de' ? 'Speichern' : 'Save'}
                                                    data-testid={`coach-history-edit-save-${conv.id}`}
                                                >
                                                    {savingTitle ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); cancelEditing(); }}
                                                    disabled={savingTitle}
                                                    className="p-1.5 rounded-md text-muted-foreground hover:bg-muted disabled:opacity-50"
                                                    title={language === 'de' ? 'Abbrechen' : 'Cancel'}
                                                    aria-label={language === 'de' ? 'Abbrechen' : 'Cancel'}
                                                    data-testid={`coach-history-edit-cancel-${conv.id}`}
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="text-sm font-medium truncate">
                                                {isSearchMode ? highlightMatch(titleText, searchQuery.trim()) : titleText}
                                            </p>
                                        )}
                                        {isSearchMode && conv.snippet && !isEditing && (
                                            <p className="text-[11px] text-muted-foreground mt-1 leading-snug line-clamp-2">
                                                <span className="font-semibold mr-1">
                                                    {conv.snippet_role === 'user'
                                                        ? (language === 'de' ? 'Du:' : 'You:')
                                                        : 'Nanouk:'}
                                                </span>
                                                {highlightMatch(conv.snippet, searchQuery.trim())}
                                            </p>
                                        )}
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {conv.message_count} {language === 'de' ? 'Nachrichten' : 'messages'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {!isEditing && !isResuming && (
                                            <button
                                                onClick={(e) => startEditing(conv, e)}
                                                className="p-1.5 rounded-lg hover:bg-primary/10 hover:text-primary transition-colors text-muted-foreground"
                                                title={language === 'de' ? 'Umbenennen' : 'Rename'}
                                                aria-label={language === 'de' ? 'Umbenennen' : 'Rename'}
                                                data-testid={`coach-history-rename-${conv.id}`}
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                        {isResuming ? (
                                            <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                        ) : !isEditing && (
                                            <button
                                                onClick={(e) => handleDelete(conv, e)}
                                                className="p-1.5 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-colors"
                                                title={language === 'de' ? 'Löschen' : 'Delete'}
                                                aria-label={language === 'de' ? 'Löschen' : 'Delete'}
                                                data-testid={`coach-history-delete-${conv.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </SheetContent>
        </Sheet>
    );
}
